<!--This is for displaying the Sync Status icon and text -->
<template>
	<div
		:title="syncBarStates[syncState].toolTipText"
		class="flex cursor-pointer flex-row items-center"
	>
		<!-- Dynamic icon depending on syncing and online state -->
		<component
			:is="syncBarStates[syncState].icon"
			class="mr-1 size-3"
			:class="syncBarStates[syncState].iconClass"
		/>

		<!-- Dynamic text depending on syncing and online state -->
		<div
			class="text-[10px] font-normal tracking-wide text-slate-400"
			:class="syncBarStates[syncState].textStyle"
		>
			{{ syncBarStates[syncState].textContent }}
			<span v-if="shouldShowSyncingPercentage"> {{ percentComplete }}% </span>
		</div>
	</div>
</template>

<script setup lang="ts">
import SlashCloudIcon from '@/components/icons/SlashCloudIcon.vue'
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import { type Component, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
	syncState: {
		type: String,
		required: true,
	},
	updatedDate: {
		type: String,
	},
	pendingPlansCount: {
		type: Number,
	},
	percentComplete: { type: Number },
})

const { t } = useI18n()

type SyncStateProperties = {
	icon: Component
	textContent: string
	textStyle?: string
	iconClass?: string
	toolTipText?: string
}

const syncBarStates = computed<Record<string, SyncStateProperties>>(() => ({
	OfflineNoSync: {
		icon: SlashCloudIcon,
		textContent: t('home.sync.lastUpdated', { date: props.updatedDate }),
		iconClass: 'text-red-500',
		toolTipText: t('home.sync.offlineNoSyncToolTip', {
			date: props.updatedDate,
		}),
	},
	OfflinePendingSync: {
		icon: SlashCloudIcon,
		textContent: t('home.sync.unsyncedPlans', {
			number: props.pendingPlansCount,
		}),
		textStyle: 'text-yellow-700 font-semibold uppercase',
		iconClass: 'text-red-500',
		toolTipText: t('home.sync.offlinePendingSyncToolTip', {
			number: props.pendingPlansCount,
		}),
	},
	OnlineNoSync: {
		icon: ArrowPathIcon,
		textContent: t('home.sync.lastUpdated', { date: props.updatedDate }),
		toolTipText: t('home.sync.onlineNoSync', { date: props.updatedDate }),
	},
	OnlineSyncing: {
		icon: ArrowPathIcon,
		textContent: t('home.sync.syncing'),
		textStyle: 'uppercase',
		toolTipText: t('home.sync.syncing'),
		iconClass: 'animate-spin',
	},
	OnlinePendingSync: {
		icon: ExclamationTriangleIcon,
		textContent: t('home.sync.unsyncedPlans', {
			number: props.pendingPlansCount,
		}),
		textStyle: 'text-yellow-700 font-semibold uppercase',
		iconClass: 'text-yellow-400',
		toolTipText: t('home.sync.onlinePendingSync', {
			number: props.pendingPlansCount,
		}),
	},
}))

const shouldShowSyncingPercentage = computed(
	() =>
		props.syncState === 'OnlineSyncing' && (props.percentComplete ?? 100) < 100
)
</script>
